<template>
  <el-dialog
    id="manageModal"
    :title="$t('leader.coworker.addCoworkers')"
    custom-class="manageDialog"
    :visible.sync="showLocal"
    :width="dialogWidth"
    :show-close="false"
  >
    <!-- Temp table -->
    <div>
      <el-table
        ref="invoiceTable"
        v-loading="loading"
        :data="formattedRespondents"
        class="invoiceTable"
        :empty-text="$t('leader.coworker.noDataYet')"
      >
        <!-- Relationship -->
        <el-table-column
          prop="relationship"
          :label="$t('leader.coworker.relationship')"
          class-name="breakoutGroup"
          min-width="110"
        >
          <template slot-scope="scope">
            {{ scope.row.groupName }}
          </template>
        </el-table-column>
        <!-- Email -->
        <el-table-column
          :label="$t('common.email')"
          prop="email"
        >
          <template slot-scope="scope">
            <div>
              {{ scope.row.email }}
            </div>
            <div
              v-if="scope.row.error"
              class="customError"
            >
              {{ scope.row.error }}
            </div>
          </template>
        </el-table-column>
        <!-- Delete button -->
        <el-table-column
          width="80"
        >
          <template slot-scope="scope">
            <el-button
              circle
              size="mini"
              plain
              type="danger"
              icon="el-icon-delete"
              @click="removeStakeholder(scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <!-- /Temp table -->

    <div class="inputArea">
      <el-form
        ref="surveyForm"
        :model="inputForm"
        :rules="rules"
        @submit.native.prevent="validateForm"
      >
        <el-row
          :gutter="10"
        >
          <el-col :span="responsiveCol.one">
            <el-form-item
              :label="$t('leader.coworker.relationship')"
              prop="relationship"
            >
              <el-select
                v-model="inputForm.relationship"
              >
                <el-option
                  v-for="(group, gKey) in groupList"
                  :key="`group_${gKey}`"
                  :label="group.text"
                  :value="group.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="responsiveCol.two">
            <el-form-item
              :label="$t('common.emails')"
              prop="emails"
            >
              <el-input
                v-model="inputForm.emails"
                :type="['laptop', 'desktop'].includes($mq) ? 'text' : 'textarea'"
                :placeholder="$t('leader.coworker.emailsPlaceholder')"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <div class="addListButtonWrap">
          <el-button
            class="addListButton addBtn"
            round
            @click="validateForm"
          >
            {{ $t('common.add') }}
          </el-button>
        </div>
      </el-form>
    </div>

    <div
      slot="footer"
    >
      <el-row
        class="buttonWrap"
      >
        <el-col :span="12">
          <el-button
            class="firstBtn"
            round
            @click="closeModal"
          >
            {{ $t('common.discard') }}
          </el-button>
        </el-col>
        <el-col :span="12">
          <el-button
            v-loading="loading"
            class="secondBtn"
            round
            type="primary"
            @click="confirmSave"
          >
            {{ $t('common.save') }}
          </el-button>
        </el-col>
      </el-row>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';
import DialogSetting from '@/mixins/dialog';
import emailValidator from '@/mixins/emailValidator';
import api from '@/api';

export default {
  mixins: [
    DialogSetting,
    emailValidator,
  ],

  props: {
    mainStakeholders: {
      type: Array,
      default: null,
    },
  },

  data() {
    const that = this;
    return {
      loading: false,
      groupList: [
        { text: this.$t('stakeholderGroup.manager'), value: 'manager' },
        { text: this.$t('stakeholderGroup.direct_report'), value: 'direct_report' },
        { text: this.$t('stakeholderGroup.peer'), value: 'peer' },
        { text: this.$t('stakeholderGroup.other'), value: 'other' },
      ],

      stakeholders: [],
      inputForm: {
        relationship: '',
        emails: '',
        language: '',
      },

      formFlag: true,
      rules: {
        relationship: [
          { required: true, message: this.$t('leader.coworker.relationshipRequired'), trigger: 'blur' },
          { validator: that.validateRelationship, trigger: 'blur' },
        ],
        emails: [
          { required: true, message: this.$t('leader.coworker.emailsRequired'), trigger: 'blur' },
          { validator: that.validateEmails, trigger: 'blur' },
          { validator: that.checkEmailsLeader, trigger: 'blur' },
          { validator: that.checkEmailsInput, trigger: 'blur' },
          { validator: that.checkEmailsRespondents, trigger: 'blur' },
        ],
      },

      errorFields: [],
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      role_type: (state) => state.user.user.role_type,
    }),

    formattedRespondents() {
      const res = this.stakeholders.concat().sort((a, b) => {
        const aIndex = this.groupList.findIndex((g) => g.value === a.org_relationship);
        const bIndex = this.groupList.findIndex((g) => g.value === b.org_relationship);

        return aIndex - bIndex;
      });

      if (this.errorFields && this.errorFields.length) {
        this.errorFields.forEach((el, index) => {
          if (el.error && el.error.length) {
            // eslint-disable-next-line prefer-destructuring
            res[index].error = el.error.join(', ');
          } else if (el.email && el.email.length) {
            // eslint-disable-next-line prefer-destructuring
            res[index].error = el.email.join(', ');
          } else {
            res[index].error = null;
          }
        });
      }

      return res;
    },

    formattedEmailsInput() {
      if (!this.inputForm.emails) return [];

      return this.inputForm.emails
        .replace(/\s/g, '')
        .replace(/\n/g, '')
        .split(',')
        .filter((el) => el !== '');
    },

    responsiveCol() {
      const col = {};
      if (['laptop', 'desktop'].includes(this.$mq)) {
        col.one = 8;
        col.two = 16;
      } else {
        col.one = 24;
        col.two = 24;
      }
      return col;
    },
  },

  watch: {
    showLocal(val) {
      if (!val) {
        this.resetForm();
      }
    },
  },

  methods: {
    // Validators
    validateEmail(rule, value, callback) {
      if (value === this.user.user.email) {
        callback(new Error(this.$t('common.rule.email.inputDifferent')));
      } else {
        callback();
      }
    },

    validateRelationship(rule, value, callback) {
      if (value === this.inputForm.relationship) {
        callback();
      } else {
        callback(new Error(this.$t('leader.coworker.pleaseSelectRelationship')));
      }
    },

    async validateForm(queue) {
      try {
        const flag = await this.$refs.surveyForm.validate();
        if (flag) {
          this.addStakeholder(queue);
        } else {
          this.$message.error(this.$t('leader.coworker.pleaseCheckFields'));
        }
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },
    // /Validators

    resetForm() {
      if (this.$refs.surveyForm) {
        this.$refs.surveyForm.resetFields();
      }
      this.stakeholders = [];
    },

    closeModal() {
      this.showLocal = false;
      this.resetForm();
      this.resetErrorField();
    },

    async addStakeholder(queue) {
      try {
        this.resetErrorField();
        const relationshipInfo = this.groupList
          .find((el) => el.value === this.inputForm.relationship);
        const foundation = this.formattedEmailsInput.map((email) => ({
          email,
          org_relationship: this.inputForm.relationship,
          groupName: relationshipInfo
            ? relationshipInfo.text : this.inputForm.relationship.replace('_', ' '),
        }));
        this.stakeholders = [...this.stakeholders, ...foundation];
        this.$refs.surveyForm.resetFields();
        if (queue === 'save') {
          this.confirmSave();
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        } else {
          this.$message.error(this.$t('leader.coworker.errorOccurred'));
        }
      } finally {
        this.loading = false;
      }
    },

    removeStakeholder(obj) {
      this.resetErrorField();
      const index = this.stakeholders.findIndex((el) => el.email === obj.email);
      if (index > -1) {
        this.stakeholders.splice(index, 1);
      }
    },

    async confirmSave() {
      if (this.inputForm.emails && this.inputForm.emails.length) {
        await this.validateForm('save');
      } else {
        this.$confirm(
          this.$t('leader.coworker.confirmAdd'),
          '', {
            dangerouslyUseHTMLString: true,
            confirmButtonText: this.$t('common.save'),
            cancelButtonText: this.$t('common.goBack'),
            roundButton: true,
            customClass: 'stakeholderConfirmation',
          },
        ).then(() => {
          this.saveStakeholders();
        }).catch((err) => {
          if (err.msg) {
            this.$message.error(err.msg);
          }
          console.log(err);
        });
      }
    },

    async saveStakeholders() {
      try {
        this.loading = true;
        this.resetErrorField();
        const sendData = this.stakeholders.map((el) => {
          const res = {};
          Object.keys(el).forEach((key) => {
            if (key !== 'groupName') { res[key] = el[key]; }
          });
          return res;
        });

        let response;
        if (this.role_type === 'coach') {
          response = await api.postLeaderCoworker(this.$route.params.id, sendData);
        } else {
          response = await api.createStakeholder(sendData);
        }
        if (response && response.data) {
          this.$emit('update:mainStakeholders', response.data);
        }
        this.$emit('reload');

        this.resetForm();
        this.showLocal = false;
      } catch (err) {
        console.log(err);
        if (err.field) {
          this.errorFields = err.field;
          this.$message.error(this.$t('leader.coworker.pleaseCheckAlert'));
        } else if (err.msg) {
          this.$message.error(err.msg);
        } else {
          this.$message.error(this.$t('leader.coworker.errorOccurred'));
        }
      } finally {
        this.loading = false;
      }
    },

    resetErrorField() {
      this.errorFields = [];
    },
  },
};
</script>

<style lang="scss" scoped>
  #manageModal {
    .invoiceTable {
      .customError {
        font-size: 12px;
        color: #f56c6c;
      }
    }

    .manageDialog {
      .el-dialog__body {
        .el-form {
          padding: 10px 25px;
          box-sizing: border-box;
          width: 100%;

          .addListButtonWrap {
            width: 100%;
            margin: 0 auto;
            text-align: center;

            .addListButton {
              width: 100%;
              max-width: 150px;
            }

            .addBtn {
              background: transparent;
              border: 2px solid #66b1ff;
              color: #66b1ff;
              font-weight: bold;

              &:hover {
                background: rgba(102, 177, 255, 0.3);
              }
              &:active {
                background: rgba(102, 177, 255, 0.5);
              }
            }
          }
        }

        .inputArea {
          background: rgba(196, 196, 196, 0.15);
          min-height: 40px;
        }
      }

      .el-dialog__footer {
        .buttonWrap {
          width: 100%;
          text-align: center;
          background: white;

          .el-col {
            .el-button {
              width: 90%;
            }
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  #manageModal {
    overflow: auto;

    .manageDialog {
      margin-top: 5vh !important;

      .el-dialog__body {
        box-sizing: border-box;
        min-height: 100px;

        .el-table {
          color: #303133;

          tr {
            color: #303133;
          }

          .el-table__empty-text {
            color: #c1c1c1;
          }
        }

        .el-form {
          .el-input {
            input {
              border-radius: 5px;
            }
          }

          .el-select {
            width: 100%;
          }

          .el-form-item {
            .el-form-item__content {
              line-height: 1.3em;
            }
            .el-input__inner {
              // padding-left: 0;
            }

            &.is-error {
              .el-input__inner {
                border-bottom-color: #ffb8b8;
              }

              .el-form-item__error {
                display: contents;
                color: #ffb8b8;
                font-size: 12px;
              }
            }
          }
        }
      }

      .el-dialog__footer {
        text-align: center;
      }
    }
  }
</style>
